// A2F-764: collapsible section card shell for the v2 left column.
//
// One per section (Doc type · Formatting · Tables · Packs · Rules ·
// Save & install). Open/close state is owned by the parent
// (ProV2Page) so cross-section behaviors — like "expanding section X
// auto-focuses ribbon button Y" — can be expressed in one place.
//
// Status pill on the right of the head:
//   default-state  → green  "Default"
//   customized     → orange "Customized"
//   override       → caller-supplied node (used by Save & install for
//                    the live counter, and Doc type for the selected
//                    label).
//
// Focus emission: when the card is open, hovering or clicking inside
// the body fires onFocus(id). The hook upstream throttles to once per
// section per session.

import { useId } from 'react'
import { ChevronDown } from 'lucide-react'
import type { ReactNode } from 'react'
import type { SectionId } from './useFocusedSection'

interface SectionCardProps {
  id: SectionId
  number: number
  title: string
  subtitle: string
  isOpen: boolean
  edited: boolean
  /** Renders in place of the default Default/Customized pill when set. */
  statusOverride?: ReactNode
  onToggle: () => void
  onFocus: (id: SectionId) => void
  children: ReactNode
}

export function SectionCard({
  id,
  number,
  title,
  subtitle,
  isOpen,
  edited,
  statusOverride,
  onToggle,
  onFocus,
  children,
}: SectionCardProps) {
  const bodyId = useId()

  return (
    <section
      className={`rounded-xl border bg-white transition-shadow ${
        isOpen
          ? 'border-slate-200 shadow-[0_2px_6px_rgba(34,22,16,0.04)]'
          : 'border-slate-200'
      }`}
      onMouseEnter={() => {
        if (isOpen) onFocus(id)
      }}
    >
      <button
        type="button"
        onClick={() => {
          onToggle()
          // Always emit focus on toggle so the first open of a section
          // pulses the ribbon button immediately, not on the next mouse
          // move. Throttle in the hook keeps telemetry quiet.
          if (!isOpen) onFocus(id)
        }}
        aria-expanded={isOpen}
        aria-controls={bodyId}
        className="w-full flex items-center gap-3 px-5 py-4 text-left"
      >
        <span
          aria-hidden
          className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-[12px] font-semibold ${
            edited
              ? 'bg-brand-orange text-white'
              : 'bg-slate-100 text-slate-500'
          }`}
        >
          {number}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-medium text-brand-charcoal truncate">
            {title}
          </span>
          <span className="block text-[12px] text-slate-500 truncate">
            {subtitle}
          </span>
        </span>
        {statusOverride ? (
          <span className="text-[11px]">{statusOverride}</span>
        ) : (
          <span
            className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ${
              edited
                ? 'bg-brand-orange/10 text-brand-orange'
                : 'bg-emerald-50 text-emerald-700'
            }`}
          >
            {edited ? 'Customized' : 'Default'}
          </span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen ? (
        <div
          id={bodyId}
          className="px-5 pb-5 pt-1 border-t border-slate-100"
          onClick={() => onFocus(id)}
        >
          {children}
        </div>
      ) : null}
    </section>
  )
}
